import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import Button from "../components/base/Button";
import FormField from "../components/FormField";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Logo } from "../components/App";
import Icons from "../components/Icons";
import { passwordSchema } from "../shared/utils/formikValidators";
import { errorMessage } from "../shared/utils/validators";
import { Alert, Snackbar } from "@mui/material";
import apiConfig from "../config/api.config";
import appConfig from "../shared/js/app-config";
import MyAccountService from "../services/MyAccountService";
import ServicesService from "../services/ServicesService";
document.title = "Orkaaps Accounts";

function SignInPage() {
  const [visible, setVisible] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const domain = apiConfig.BASE_URL;
  const url = `${domain}/signin`;

  async function handleSubmit(values) {
    setFormSubmitted(true);
    try {
      await axios.post(
        url,
        new URLSearchParams({
          username: values.userName.trim(),
          password: values.password.trim(),
          type: "user",
        }),
        {
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          withCredentials: true,
        }
      );
      await appConfig.fetch();
      const account = await MyAccountService.fetch();
      const organizationId = account.getOrganizationId();
      if (organizationId == null) {
        if (window.location.pathname !== "/create-service") {
          window.location.replace("/create-service");
        }
        return;
      }
      const service = await ServicesService.getOrganizationServices({
        organizationId,
      });
      if (service.length === 0) {
        if (window.location.pathname !== "/create-service") {
          window.location.replace("/create-service");
        }
        return;
      }
      window.location.replace("/");
    } catch (error) {
      const message = errorMessage(error);
      setSnackbar({
        open: true,
        message: message || "Something went wrong",
        severity: "error",
      });
    }
  }

  function snackBar() {
    return (
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    );
  }

  const $userName = useRef();
  const formik = useFormik({
    initialValues: {
      userName: "",
      password: "",
    },
    validationSchema: Yup.object().shape({
      userName: Yup.string()
        .trim()
        .required("Required mobile or email")
        .test(
          "mobile-or-email",
          "Enter a valid email address or 10 digit mobile number",
          value => {
            if (!value) return false;
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            const mobileRegex = /^[0-9]{10}$/;
            return emailRegex.test(value) || mobileRegex.test(value);
          }
        ),
      password: passwordSchema,
    }),
    onSubmit: handleSubmit,
  });

  useEffect(() => {
    $userName.current.focus();
  }, []);

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 400,
          background: "#fff",
          borderRadius: 16,
          boxShadow: '0 2px 16px rgba(215, 225, 255, 0.94)',
          padding: "32px 24px",
          margin: "32px 0",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Logo />
        <div
          style={{
            marginTop: 16,
            marginBottom: 8,
            fontWeight: 700,
            fontSize: 22,
            color: "#222",
          }}
        >
          Sign in to Orkaaps
        </div>
        <div
          style={{
            color: "#666",
            fontSize: 15,
            marginBottom: 24,
            textAlign: "center",
          }}
        >
          Welcome back! Enter your credentials to access your whatsapp account.
        </div>
        <form onSubmit={formik.handleSubmit} style={{ width: "100%" }}>
          <div style={{ marginBottom: 18 }}>
            <FormField
              ref={$userName}
              type="auth-text"
              name="userName"
              placeholder="Whatsapp Number"
              value={formik.values.userName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                (formSubmitted || formik.submitCount > 0) &&
                formik.touched.userName &&
                formik.errors.userName
              }
              style={{
                fontSize: 15,
                padding: "10px 12px",
                borderRadius: 8,
                border: "1px solid #e0e0e0",
                background: "#f8f9fa",
              }}
            />
          </div>
          <div style={{ marginBottom: 18 }}>
            <FormField
              autoComplete="off"
              name="password"
              placeholder="Password"
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                (formSubmitted || formik.submitCount > 0) &&
                formik.touched.password &&
                formik.errors.password
              }
              type={visible ? "text" : "password"}
              iconSuffix={
                formik.values.password && (
                  <div
                    style={{ cursor: "pointer" }}
                    onClick={() => setVisible(!visible)}
                  >
                    {visible ? Icons.Visible : Icons.VisibleOff}
                  </div>
                )
              }
              maxLength={25}
              style={{
                fontSize: 15,
                padding: "10px 12px",
                borderRadius: 8,
                border: "1px solid #e0e0e0",
                background: "#f8f9fa",
              }}
            />
          </div>
          {/* <div style={{ marginBottom: 18, textAlign: "right" }}>
            <a
              href="/password-reset"
              className="ui-link"
              style={{
                color: "#1976d2",
                fontSize: 14,
                textDecoration: "none",
              }}
            >
              Forgot password?
            </a>
          </div> */}
          <Button
            type="submit"
            appearance="auth-btn"
            style={{
              width: "100%",
              padding: "12px",
              fontSize: 16,
              borderRadius: 8,
              background: "#1976d2",
              color: "#fff",
              fontWeight: 600,
              border: "none",
              boxShadow: "0 2px 8px rgba(25,118,210,0.08)",
              cursor: "pointer",
            }}
          >
            Sign in
          </Button>
        </form>
        <div
          style={{
            margin: "auto",
            paddingTop: 24,
            fontSize: 15,
            textAlign: "center",
          }}
        >
          New to Orkaaps?
          <a
            style={{ marginLeft: "5px", color: "#1976d2" }}
            href="/signup"
            className="ui-link"
          >
            Get started
          </a>
        </div>
        {snackBar()}
      </div>
    </div>
  );
}
export { SignInPage };
